// @ts-nocheck
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { MessageCircle, MoreHorizontal, Pin, PinOff, Share2 } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { WallPost } from '../hooks/useWallFeed';
import { useReactionTypes, useUserReaction, usePostReaction } from '../hooks/usePostReaction';
import { useCurrentUser } from '../hooks/useCurrentUser';
import { useLoginPrompt } from './LoginPrompt';
import { usePinPost } from '@/hooks/usePinPost';
import { SharePostMenu } from './SharePostMenu';
import { usePostHeatLevel } from '../hooks/usePostHeatLevel';
import { PostHeatBadge } from './PostHeatBadge';
import { PostGlowWrapper } from './PostGlowWrapper';

interface WallPostCardProps {
  post: WallPost;
  showPinBadge?: boolean;
  onPinChange?: () => void;
}

export function WallPostCard({ post, showPinBadge = false, onPinChange }: WallPostCardProps) {
  const { user, isAuthenticated } = useCurrentUser();
  const { showLoginPrompt } = useLoginPrompt();
  const { data: reactionTypes } = useReactionTypes();
  const { data: userReaction } = useUserReaction(post.id);
  const { toggleReaction, isToggling } = usePostReaction(post.id);
  const { pinPost, unpinPost, isPinning } = usePinPost(user?.id);
  const heatLevel = usePostHeatLevel(post);
  const [showReactions, setShowReactions] = useState(false);

  const isOwner = user?.id === post.profile_id;

  const handlePinToggle = async () => {
    if (post.is_pinned) {
      const success = await unpinPost(post.id);
      if (success && onPinChange) onPinChange();
    } else {
      const success = await pinPost(post.id);
      if (success && onPinChange) onPinChange();
    }
  };

  const authorName = post.author.name
    ? post.author.name
    : post.author.username || 'Unknown';

  const authorInitials = authorName
    .split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  const relativeTime = formatDistanceToNow(new Date(post.created_at), { addSuffix: true });

  // Parse hashtags from content or use hashtags field
  const hashtags = post.hashtags || [];

  // Calculate total reactions
  const totalReactions = post.reactions_count
    ? Object.values(post.reactions_count).reduce((sum, count) => sum + count, 0)
    : post.likes_count;

  const handleReactClick = () => {
    if (!isAuthenticated) {
      showLoginPrompt('react to posts');
      return;
    }
    setShowReactions(!showReactions);
  };

  const handleCommentClick = () => {
    if (!isAuthenticated) {
      showLoginPrompt('comment on posts');
      return;
    }
    // TODO: Open comment sheet/modal
  };

  return (
    <PostGlowWrapper heatLevel={heatLevel}>
      <article className={cn(
        "bg-card border-b border-border p-4",
        showPinBadge && post.is_pinned && "bg-primary/5 border border-primary/20 rounded-lg mb-2"
      )}>
        {/* Heat Badge */}
        {heatLevel.level > 0 && (
          <div className="flex justify-end mb-2">
            <PostHeatBadge heatLevel={heatLevel} />
          </div>
        )}

        {/* Pinned Badge */}
        {showPinBadge && post.is_pinned && (
          <div className="flex items-center gap-1 text-primary text-xs font-medium mb-2">
            <Pin className="h-3 w-3" />
            Pinned post
          </div>
        )}

      {/* Author Header */}
      <div className="flex items-start justify-between mb-3">
        <Link to={`/profile/${post.author.username || post.author.id}`} className="flex items-center gap-3">
          <Avatar className="h-10 w-10">
            <AvatarImage src={post.author.avatar_url || undefined} />
            <AvatarFallback className="bg-primary/10 text-primary text-sm font-medium">
              {authorInitials}
            </AvatarFallback>
          </Avatar>
          <div>
            <p className="font-semibold text-foreground text-sm leading-tight">{authorName}</p>
            {post.author.username && (
              <p className="text-muted-foreground text-xs">@{post.author.username}</p>
            )}
          </div>
        </Link>
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">{relativeTime}</span>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {isOwner && (
                <DropdownMenuItem onClick={handlePinToggle} disabled={isPinning}>
                  {post.is_pinned ? (
                    <>
                      <PinOff className="h-4 w-4 mr-2" />
                      Unpin from Profile
                    </>
                  ) : (
                    <>
                      <Pin className="h-4 w-4 mr-2" />
                      Pin to Profile
                    </>
                  )}
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Post Content */}
      {post.content && (
        <p className="text-foreground text-sm whitespace-pre-wrap mb-3">
          {post.content}
        </p>
      )}

      {/* Hashtags */}
      {hashtags.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-3">
          {hashtags.map((tag, index) => (
            <Link
              key={index}
              to={`/discover?hashtag=${encodeURIComponent(tag)}`}
              className="text-primary text-sm hover:underline"
            >
              #{tag}
            </Link>
          ))}
        </div>
      )}

      {/* Media */}
      {post.media_urls && post.media_urls.length > 0 && (
        <div className={cn(
          "grid gap-2 mb-3 rounded-lg overflow-hidden",
          post.media_urls.length === 1 && "grid-cols-1",
          post.media_urls.length === 2 && "grid-cols-2",
          post.media_urls.length >= 3 && "grid-cols-2"
        )}>
          {post.media_urls.slice(0, 4).map((url, index) => {
            const isVideo = url.includes('.mp4') || url.includes('.webm') || url.includes('.mov');
            
            if (isVideo) {
              return (
                <video
                  key={index}
                  src={url}
                  controls
                  className="w-full aspect-video object-cover rounded-lg"
                />
              );
            }
            
            return (
              <img
                key={index}
                src={url}
                alt=""
                className={cn(
                  "w-full object-cover rounded-lg",
                  post.media_urls!.length === 1 ? "max-h-96" : "aspect-square"
                )}
              />
            );
          })}
        </div>
      )}

      {/* Reaction Summary */}
      {(totalReactions > 0 || post.comments_count > 0) && (
        <div className="flex items-center justify-between text-xs text-muted-foreground mb-2 pb-2 border-b border-border">
          <div className="flex items-center gap-1">
            {post.reactions_count && reactionTypes && (
              <span className="flex items-center gap-0.5">
                {Object.entries(post.reactions_count)
                  .sort(([, a], [, b]) => b - a)
                  .slice(0, 3)
                  .map(([typeId]) => {
                    const type = reactionTypes.find(r => r.id === typeId);
                    return type?.emoji;
                  })
                  .filter(Boolean)
                  .join('')}
              </span>
            )}
            {totalReactions > 0 && <span>{totalReactions}</span>}
          </div>
          <div className="flex gap-3">
            {post.comments_count > 0 && (
              <span>{post.comments_count} comment{post.comments_count !== 1 ? 's' : ''}</span>
            )}
            {post.share_count && post.share_count > 0 && (
              <span>{post.share_count} share{post.share_count !== 1 ? 's' : ''}</span>
            )}
          </div>
        </div>
      )}

      {/* Action Bar */}
      <div className="flex items-center justify-around pt-1">
        {/* React Button */}
        <div className="relative">
          <Button
            variant="ghost"
            size="sm"
            className={cn(
              "flex items-center gap-2 text-muted-foreground",
              userReaction && "text-primary"
            )}
            onClick={handleReactClick}
            disabled={isToggling}
          >
            {userReaction && reactionTypes ? (
              <span className="text-lg">
                {reactionTypes.find(r => r.id === userReaction.reaction_type)?.emoji || '👍'}
              </span>
            ) : (
              <span className="text-lg">👍</span>
            )}
            <span className="text-sm">React</span>
          </Button>

          {/* Reaction Picker */}
          {showReactions && reactionTypes && isAuthenticated && (
            <div className="absolute bottom-full left-0 mb-2 bg-card border border-border rounded-full px-2 py-1 flex gap-1 shadow-lg z-10">
              {reactionTypes.map((reaction) => (
                <button
                  key={reaction.id}
                  onClick={() => {
                    toggleReaction(reaction.id);
                    setShowReactions(false);
                  }}
                  className={cn(
                    "text-xl p-1 rounded-full hover:bg-muted transition-transform hover:scale-125",
                    userReaction?.reaction_type === reaction.id && "bg-primary/10"
                  )}
                  title={reaction.label}
                >
                  {reaction.emoji}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Comment Button */}
        <Button 
          variant="ghost" 
          size="sm" 
          className="flex items-center gap-2 text-muted-foreground"
          onClick={handleCommentClick}
        >
          <MessageCircle className="h-5 w-5" />
          <span className="text-sm">Comment</span>
        </Button>

        {/* Share Button */}
        <SharePostMenu postId={post.id} postContent={post.content || undefined}>
          <Button 
            variant="ghost" 
            size="sm" 
            className="flex items-center gap-2 text-muted-foreground"
          >
            <Share2 className="h-5 w-5" />
            <span className="text-sm">Share</span>
          </Button>
        </SharePostMenu>
      </div>
      </article>
    </PostGlowWrapper>
  );
}
